"use client";

import { MouseEvent, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Rating from "@mui/material/Rating";
import Divider from "@mui/material/Divider";
import TextField from "@mui/material/TextField";
import FormControlLabel from "@mui/material/FormControlLabel"; // GLOBAL CUSTOM COMPONENTS
import Checkbox from "@mui/material/Checkbox";
import Button from "@mui/material/Button";

import {
  FlexBetween,
  FlexBox,
} from "src/lib/components/salify-next-layout/default/components/flex-box";
import { H5, H6 } from "src/lib/components/salify-next-layout/default/components/Typography";
import SalifyFallback from "../../salify-fallback/default";
import { Category } from "src/lib/fetcher/client";
import useFilter from "./use-filter";
import { usePathname } from "next/navigation";
import { CategoryItem } from "src/lib/components/category-section/bazaar/side-nav-bar";
import { Typography } from "@mui/material";

const INITIAL_RATING_FILTERS_SELECT_STATE: Record<string, boolean> = {
  5: false,
  4: false,
  3: false,
};

const WEIGHT_THRESHOLD_FILTERS: Array<[number?, number?]> = [
  [undefined, 100],
  [100, 200],
  [200, 500],
  [500, undefined],
];

const INITIAL_WEIGHT_FILTERS_SELECT_STATE: Array<boolean> = WEIGHT_THRESHOLD_FILTERS.map(
  (_) => false,
);

const ProductFilterCard: React.FC<{ shouldHideFilters: boolean }> = ({ shouldHideFilters }) => {
  const categoryList = SalifyFallback.useSelect<Category[]>("output:categories.data");
  const {
    activeCategory,
    minPrice,
    maxPrice,
    setActiveCategory,
    onChangeMinPrice,
    onChangeMaxPrice,
    onFilterPriceRange,
    onFilterRating,
    onFilterWeight,
  } = useFilter();

  const [ratingFiltersSelectState, setRatingFiltersSelectState] = useState(
    INITIAL_RATING_FILTERS_SELECT_STATE,
  );

  const [weightFilterSelectState, setWeightFilterSelectState] = useState(
    INITIAL_WEIGHT_FILTERS_SELECT_STATE,
  );

  const pathname = usePathname();

  const handleFilterByRating = (event: MouseEvent) => {
    const selectedRating = (event.target as HTMLInputElement).value;
    setRatingFiltersSelectState({
      ...INITIAL_RATING_FILTERS_SELECT_STATE,
      [selectedRating]: !ratingFiltersSelectState[selectedRating],
    });
    onFilterRating(selectedRating);
  };

  const handleFilterByWeight = (event: MouseEvent) => {
    const selectedWeightRange = +(event.target as HTMLInputElement).value;
    setWeightFilterSelectState((oldState) => {
      const newState = [...INITIAL_WEIGHT_FILTERS_SELECT_STATE];
      newState[selectedWeightRange] = !oldState[selectedWeightRange];
      return newState;
    });
    onFilterWeight(WEIGHT_THRESHOLD_FILTERS[selectedWeightRange]);
  };

  useEffect(() => {
    const currCategory = new RegExp(/\/collection\/(.*)/).exec(pathname)?.[1];
    if (currCategory) {
      setActiveCategory(currCategory);
    }
  }, [pathname]);

  const renderWeightFilter = ({ lower, upper }: { lower?: number; upper?: number }) => {
    if (!lower) return `Dưới ${upper?.toFixed()}`;
    if (!upper) return `Trên ${lower?.toFixed()}`;
    return `Từ ${lower} - ${upper}`;
  };

  return (
    <Card
      sx={{
        p: "18px 27px",
        overflow: "auto",
        background: "inherit",
        border: "none",
        boxShadow: "none",
      }}
      elevation={1}
    >
      {/* CATEGORY VARIANT FILTER */}
      <H6 mb={1.25}>
        <SalifyFallback.Label path="categories" default="Danh mục" />
      </H6>

      {categoryList.map((item, ind) => (
        <CategoryItem key={ind} item={item} ind={ind} isSelected={item.code === activeCategory} />
      ))}

      {shouldHideFilters ? null : (
        <>
          <Box component={Divider} my={3} borderColor="light.light1" />

          {/* PRICE VARIANT FILTER */}
          <H6 mb={2}>
            <SalifyFallback.Label path="filterByPrice" default="Tìm theo giá" />
          </H6>

          <Typography mb="9px">
            <SalifyFallback.Label path="priceRange" default="Chọn khoảng giá" />
          </Typography>
          <FlexBox flexDirection="column" gap={2}>
            <FlexBetween>
              <TextField
                placeholder={"0"}
                onChange={onChangeMinPrice}
                value={minPrice}
                size="small"
                fullWidth
              />
              <H5 color="grey.600" px={1}>
                -
              </H5>
              <TextField
                placeholder={"0"}
                onChange={onChangeMaxPrice}
                value={maxPrice}
                size="small"
                fullWidth
              />
            </FlexBetween>
            <Button variant="outlined" color="primary" onClick={onFilterPriceRange}>
              <SalifyFallback.Label path="submitPriceRange" default="Áp dụng" />
            </Button>
          </FlexBox>

          <Box component={Divider} my={3} borderColor="light.light1" />

          {/* TODO enable BRAND VARIANT FILTER */}
          {/*<H6 mb={2}>*/}
          {/*  <SalifyFallback.Label path="brands" default="Thương hiệu" />*/}
          {/*</H6>*/}

          {/* {brandList.map((item) => (
        <FormControlLabel
          key={item}
          sx={{
            display: "flex",
          }}
          label={<Span color="inherit">{item}</Span>}
          control={<Checkbox size="small" color="secondary" />}
        />
      ))}

      <Box component={Divider} my={3}  borderColor="light.light1" /> */}

          {/* SALES OPTIONS */}
          {/* {otherOptions.map((item) => (
        <FormControlLabel
          key={item}
          sx={{
            display: "flex",
          }}
          label={<Span color="inherit">{item}</Span>}
          control={<Checkbox size="small" color="secondary" />}
        />
      ))}

      <Box component={Divider} my={3}  borderColor="light.light1" /> */}

          {/* WEIGHT FILTER */}
          <H6 mb={2}>
            <SalifyFallback.Label path="filterByWeight" default="Tìm theo trọng lượng (gr/ml)" />
          </H6>
          <FlexBox flexDirection="column">
            {WEIGHT_THRESHOLD_FILTERS.map(([lower, upper], idx) => (
              <FormControlLabel
                key={`filter-weight-${idx}`}
                control={
                  <Checkbox
                    size="small"
                    checked={weightFilterSelectState[idx]}
                    onClick={handleFilterByWeight}
                  />
                }
                value={idx}
                label={
                  <Box display="flex" alignItems="center">
                    <Box sx={{ ml: 1 }}>{renderWeightFilter({ lower, upper })}</Box>
                  </Box>
                }
              />
            ))}
          </FlexBox>

          <Box component={Divider} my={3} borderColor="light.light1" />

          {/* RATINGS FILTER */}
          <H6 mb={2}>
            <SalifyFallback.Label path="rating" default="Đánh giá" />
          </H6>
          {Object.keys(INITIAL_RATING_FILTERS_SELECT_STATE)
            .toSorted((a, b) => +b - +a)
            .map((item) => (
              <FormControlLabel
                key={`filter-rating-${item}`}
                control={
                  <Checkbox
                    size="small"
                    checked={ratingFiltersSelectState[item]}
                    onClick={handleFilterByRating}
                  />
                }
                value={item}
                label={
                  <Box display="flex" alignItems="center">
                    <Rating size="small" value={+item} color="primary" readOnly />
                    <Box sx={{ ml: 1 }}>
                      <SalifyFallback.LabelTemplate
                        path="Rating From"
                        default="Từ {{rating}} sao"
                        params={{ rating: item }}
                      />
                    </Box>
                  </Box>
                }
              />
            ))}

          {/* <Box component={Divider} my={3}  borderColor="light.light1" /> */}

          {/* COLORS VARIANT FILTER */}
          {/* <H6 mb={2}>
        <SalifyFallback.Label path="colors" default="Màu sắc" />
      </H6>

      <FlexBox mb={2} flexWrap="wrap" gap={1}>
        {colorList.map((item) => (
          <Box
            key={item}
            width={25}
            height={25}
            flexShrink={0}
            bgcolor={item}
            borderRadius="50%"
            sx={{
              cursor: "pointer",
            }}
          />
        ))}
      </FlexBox> */}
        </>
      )}
    </Card>
  );
};

export default ProductFilterCard;
